import { createSlice, PayloadAction, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { RootState, store } from '../../redux/store';
import { AccessRequest, AccessResponse, AdminRequest, Admin } from './types';
import { okaoAxios } from '../../helpers/axios';
import { AlertColor } from '@mui/material/Alert';
import { Restaurant } from '../Restaurant/types';



export interface IAdminSlice {
  accessResponse: AccessResponse | undefined
  adminRequests: AdminRequest[] | undefined
  admins: Admin[] | undefined
  adminsCount: number
  adminRequestCount: number
  status: string
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  pageSize: number
  page: number
}

const initialState: IAdminSlice = {
  status: 'idle',
  accessResponse: undefined,
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  adminRequests: undefined,
  pageSize: 100,
  adminRequestCount: 0,
  admins: undefined,
  adminsCount: 0,
  page: 0
};

export const requestAdminAccess = createAsyncThunk('api/requestAdminAccess', async (req: AccessRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/request-access`, req)
  return response;
})

export const fetchAdminRequests = createAsyncThunk('api/fetchAdminRequests', async (page: number) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/access-request/${page}?size=${store.getState().admin.pageSize}`)
  return response;
})

export const fetchAdmins = createAsyncThunk('api/fetchAdmins', async (page: number) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/${page}?size=${store.getState().admin.pageSize}`)
  return response;
})

export const grantAdminAccess = createAsyncThunk('api/grantAdminAccess', async (req: AccessRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/grant-access`, req)
  return response;
})

export const declineAdminAccess = createAsyncThunk('api/declineAdminAccess', async (req: AccessRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/decline-access`, req)
  return response;
})

export const deleteAdmin = createAsyncThunk('api/deleteAdmin', async (req: AccessRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/delete`, req)
  return response;
})

export const updateAdmin = createAsyncThunk('api/updateAdmin', async (req: AccessRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).put(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin`, req)
  return response;
})

export const manualRestaurantAdd = createAsyncThunk<
  Restaurant,
  Restaurant
>(
  "api/manualRestaurantAdd",
  async (restaurant: Restaurant) => {
    const auth = store.getState().secure.accessToken ?? "";
    const response = await okaoAxios(auth).post<Restaurant>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/manual-restaurant-add`,
      restaurant
    );
    return response.data;
  }
);

export const loadRestaurantCache = createAsyncThunk('api/loadRestaurantCache', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/load-restaurants-to-cache`)
  return response;
})

export const loadReviewsCache = createAsyncThunk('api/loadReviewsCache', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/load-reviews-to-cache`)
  return response;
})

export const loadEaterCache = createAsyncThunk('api/loadEaterCache', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/load-eaters-to-cache`)
  return response;
})

// Sync to Voucherify endpoints
export const syncEatersToVoucherify = createAsyncThunk('api/syncEatersToVoucherify', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/sync-eaters-to-voucherify`)
  return response;
})

export const syncRestaurantsToVoucherify = createAsyncThunk('api/syncRestaurantsToVoucherify', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/sync-restaurants-to-voucherify`)
  return response;
})

// Embedding generation
export const generateRestaurantEmbeddings = createAsyncThunk('api/generateRestaurantEmbeddings', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/generate-restaurant-embeddings`)
  return response;
})

// Rebuild endpoints
export const rebuildEaterCaches = createAsyncThunk('api/rebuildEaterCaches', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/rebuild-eater-caches`)
  return response;
})

export const rebuildRestaurantCaches = createAsyncThunk('api/rebuildRestaurantCaches', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/rebuild-restaurant-caches`)
  return response;
})

// Cache expiry endpoints
export const expireCache = createAsyncThunk(
  'api/expireCache',
  async (cacheName: string) => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).delete(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/cache/expire/${cacheName}`
    );
  }
);

export const expireAllCaches = createAsyncThunk(
  'api/expireAllCaches',
  async () => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).delete(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/cache/expire-all`
    );
  }
);

export const refreshViewCache = createAsyncThunk(
  'api/refreshViewCache',
  async () => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/cache/views/refresh`
    );
  }
);

export const warmupGroupPredictions = createAsyncThunk(
  'api/warmupGroupPredictions',
  async () => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/warmup-group-predictions`
    );
  }
);

export const loadCensusCache = createAsyncThunk(
  'api/loadCensusCache',
  async () => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/census`
    );
  }
);

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    }
  },
  extraReducers: builder => {
    // All addCase calls must come before addMatcher calls
    builder
      .addCase(requestAdminAccess.fulfilled, (state, action) => {
        state.accessResponse = action.payload.data
        state.status = 'idle'
        state.shouldOpen = true;
        state.message = 'Successfully Requested'
        state.severity = 'success'
      })
      .addCase(expireCache.fulfilled, (state, action) => {
        state.shouldOpen = true;
        state.message = `Cache '${action.meta.arg}' expired successfully`;
        state.status = 'idle';
        state.severity = 'success';
      })
      .addCase(expireCache.rejected, (state, action) => {
        state.shouldOpen = true;
        state.message = `Failed to expire cache '${action.meta.arg}'`;
        state.status = 'error';
        state.severity = 'error';
      })
      .addCase(expireAllCaches.fulfilled, (state) => {
        state.shouldOpen = true;
        state.message = 'All caches expired successfully';
        state.status = 'idle';
        state.severity = 'success';
      })
      .addCase(expireAllCaches.rejected, (state) => {
        state.shouldOpen = true;
        state.message = 'Failed to expire all caches';
        state.status = 'error';
        state.severity = 'error';
      })
      .addCase(refreshViewCache.fulfilled, (state) => {
        state.shouldOpen = true;
        state.message = 'View cache refreshed successfully';
        state.status = 'idle';
        state.severity = 'success';
      })
      .addCase(refreshViewCache.rejected, (state) => {
        state.shouldOpen = true;
        state.message = 'Failed to refresh view cache';
        state.status = 'error';
        state.severity = 'error';
      })
      .addCase(warmupGroupPredictions.fulfilled, (state) => {
        state.shouldOpen = true;
        state.message = 'Group recommendations cache refreshed successfully';
        state.status = 'idle';
        state.severity = 'success';
      })
      .addCase(warmupGroupPredictions.rejected, (state) => {
        state.shouldOpen = true;
        state.message = 'Failed to refresh group recommendations cache';
        state.status = 'error';
        state.severity = 'error';
      })
      .addCase(loadCensusCache.fulfilled, (state) => {
        state.shouldOpen = true;
        state.message = 'Census data load started';
        state.status = 'idle';
        state.severity = 'success';
      })
      .addCase(loadCensusCache.rejected, (state) => {
        state.shouldOpen = true;
        state.message = 'Failed to load census data';
        state.status = 'error';
        state.severity = 'error';
      })
      // Now add all addMatcher calls
      .addMatcher(isAnyOf(fetchAdminRequests.fulfilled, grantAdminAccess.fulfilled, declineAdminAccess.fulfilled), (state, action) => {
        state.adminRequests = action.payload.data
        state.adminRequestCount = action.payload.data.length
        state.status = 'idle'
      })
      .addMatcher(isAnyOf(fetchAdmins.fulfilled, deleteAdmin.fulfilled, updateAdmin.fulfilled), (state, action) => {
        state.admins = action.payload.data
        state.adminsCount = action.payload.data.length
        state.status = 'idle'
      })
      .addMatcher(isAnyOf(deleteAdmin.rejected, updateAdmin.rejected, requestAdminAccess.pending, fetchAdminRequests.pending, grantAdminAccess.pending, declineAdminAccess.pending), (state, action) => {
        state.status = 'pending'
      })
      .addMatcher(isAnyOf(deleteAdmin.rejected, updateAdmin.rejected, requestAdminAccess.rejected, fetchAdminRequests.rejected, grantAdminAccess.rejected, declineAdminAccess.rejected), (state, action) => {
        state.shouldOpen = true;
        state.message = 'Error occurred while performing a admin action'
        state.status = 'error'
        state.severity = 'error'
        console.log(action.error);
      })
  }
});

export const selectAccessResponse = (state: RootState): AccessResponse | undefined => state.admin.accessResponse
export const selectAdminRequests = (state: RootState): AdminRequest[] | undefined => state.admin.adminRequests
export const selectAdmins = (state: RootState): Admin[] | undefined => state.admin.admins
export const selectAdminRequestRowCount = (state: RootState): number => state.admin.adminRequestCount
export const selectAdminRowCount = (state: RootState): number => state.admin.adminsCount
export const selectShouldOpen = (state: RootState): boolean => state.admin.shouldOpen
export const selectMessage = (state: RootState): string | undefined => state.admin.message
export const selectSeverity = (state: RootState): AlertColor | undefined => state.admin.severity
export const selectIsLoading = (state: RootState): boolean => state.admin.status === "loading"
export const selectPageSize = (state: RootState): number => state.admin.pageSize
export const selectPage = (state: RootState): number => state.admin.page

export const { setOpen, setPageSize, setPage } = adminSlice.actions;
export default adminSlice.reducer
